'use client';

import Link from 'next/link';
import { Calendar, FileText } from 'lucide-react';
import { LazyImage } from '../LazyImage';
import { CONTENT_TYPE_CONFIG } from '@sizzlenz/shared';
import { getContentDisplayDate, getContentUrl } from '@/lib/utils';
import type { ApiContent } from '@/lib/api/types';

interface ContentListItemProps {
  content: ApiContent;
  href?: string;
  backLabel?: string;
  backHref?: string;
  /** Show the thumbnail image. Defaults to true. */
  showImage?: boolean;
}

function formatDate(d: string | null | undefined) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function buildHref(base: string, backLabel?: string, backHref?: string): string {
  if (!backLabel && !backHref) return base;
  const params = new URLSearchParams();
  if (backLabel) params.set('backLabel', backLabel);
  if (backHref) params.set('backHref', backHref);
  return `${base}?${params.toString()}`;
}

export function ContentListItem({
  content,
  href,
  backLabel,
  backHref,
  showImage = true,
}: ContentListItemProps) {
  const typeConfig = CONTENT_TYPE_CONFIG[content.type as keyof typeof CONTENT_TYPE_CONFIG];
  const baseHref = href || getContentUrl(content);
  const linkHref = buildHref(baseHref, backLabel, backHref);
  const displayDate = getContentDisplayDate(content);

  return (
    <Link href={linkHref} className="block">
      <div className="group flex gap-4 bg-white rounded-xl border border-gray-100 p-3 hover:shadow-md transition-shadow cursor-pointer">
        {/* Thumbnail */}
        {showImage && (
          <div className="relative shrink-0 w-24 h-24 sm:w-32 sm:h-24 rounded-lg overflow-hidden bg-gray-50">
            <LazyImage
              src={content.image || ''}
              alt={content.title || 'Content'}
              aspectRatio="1/1"
              className="w-full h-full"
              objectFit="cover"
              fallback={
                <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-blue-50 to-cyan-50">
                  <FileText className="w-6 h-6 text-blue-300" />
                </div>
              }
            />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-1">
            {typeConfig && (
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${typeConfig.color}`}
              >
                {typeConfig.label}
              </span>
            )}
            {displayDate && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3 h-3" />
                {formatDate(displayDate)}
              </span>
            )}
          </div>
          <h3 className="text-sm sm:text-base font-bold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {content.title || 'Untitled'}
          </h3>
          {content.blurb && (
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-0.5">{content.blurb}</p>
          )}
          {content.tags && content.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {content.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded-md font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
